import { useCallback, useEffect, useState } from 'react';
import type * as React from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError, getApiErrorMessage } from './api';
import {
  AuditPage,
  DashboardPage,
  LaunchAnnouncementsPage,
  ProactiveInboxPage,
  ProactiveReportsPage,
  ProactiveReviewPage,
  ProductCategoriesPage,
  ProductMasterWorkItemPage,
  ProductMasterWorkItemsPage,
  ProductSubcategoriesPage,
  ReportsPage,
  RoundEditorPage,
  RoundsPage,
  SettingsPage,
  VendorSubmissionPage,
  VendorApplicationPageV2,
  VendorApplicationsPage,
  VendorPortalPage,
  VendorProposalEditorPage,
  VendorChangeRequestsPage,
  VendorsPage,
  type Notify,
} from './pages';
import { FormalProductMastersPage } from './pages/formal-product-masters';
import { NewProductCompetitorComparePage as ComparePage } from './pages/competitor-compare';
import { InternalPasswordResetPage } from './pages/internal-password-reset';
import { UpgradedMeetingModePage as MeetingPage } from './pages/meeting-mode';
import { Button, Card, Input, Loading, Logo, Shell, Toast } from './ui';
import type { User } from './shared/types';
import { VENDOR_PLATFORM_NAME } from './shared/vendor-self-service';

function LoginPage({ onLogin, notify }: { onLogin: (user: User) => void; notify: Notify }) {
  const [username, setUsername] = useState(import.meta.env.DEV ? 'buyer' : '');
  const [password, setPassword] = useState(import.meta.env.DEV ? 'DemoPass123!' : '');
  const [loading, setLoading] = useState(false);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      const result = await api.auth.login(username, password);
      onLogin(result.user);
      notify('登入成功，歡迎回來。');
    } catch (error) {
      const message = getApiErrorMessage(error, '登入失敗，請稍後再試。');
      notify(
        error instanceof ApiError && error.requestId
          ? `${message}（錯誤編號：${error.requestId}）`
          : message,
        'error',
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="relative min-h-screen overflow-hidden bg-ink">
      <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-teal-500/20 blur-3xl" />
      <div className="absolute -bottom-40 -right-20 h-[32rem] w-[32rem] rounded-full bg-amber-300/10 blur-3xl" />
      <main className="relative mx-auto grid min-h-screen max-w-6xl items-center gap-10 px-6 py-10 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="text-white">
          <div className="mb-12 flex items-center gap-3">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-teal-500 text-xl font-extrabold">
              P
            </span>
            <div>
              <p className="text-sm font-extrabold tracking-[0.2em]">PHARMAFLOW</p>
              <p className="text-xs text-white/55">{VENDOR_PLATFORM_NAME}</p>
            </div>
          </div>
          <p className="text-sm font-bold uppercase tracking-[0.24em] text-teal-300">
            NEW PRODUCT QUOTATION
          </p>
          <h1 className="mt-4 max-w-xl text-4xl font-extrabold leading-tight md:text-6xl">
            讓每一個新品提案，都有清楚的下一步。
          </h1>
          <p className="mt-6 max-w-xl text-base leading-8 text-white/65">
            集中收集廠商報價、採購審核與會議決議，讓會議不再追著散落的附件與試算表跑。
          </p>
          <div className="mt-10 grid max-w-xl gap-3 text-sm text-white/75 sm:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <strong className="block text-2xl text-teal-300">01</strong>
              <span className="mt-2 block">專屬邀請填報</span>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <strong className="block text-2xl text-teal-300">02</strong>
              <span className="mt-2 block">審核與會議決議</span>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <strong className="block text-2xl text-teal-300">03</strong>
              <span className="mt-2 block">比較並留下決議</span>
            </div>
          </div>
        </div>
        <Card className="mx-auto w-full max-w-md p-7">
          <div className="mb-7">
            <Logo />
            <h2 className="mt-8 text-2xl font-extrabold">登入內部系統</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              採購與門市使用內部帳號登入；廠商請使用專屬邀請連結。
            </p>
          </div>
          <form className="space-y-4" onSubmit={submit}>
            <Input
              label="帳號"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
            />
            <Input
              label="密碼"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
            />
            <Button className="mt-2 w-full" loading={loading}>
              登入
            </Button>
          </form>
          <div className="mt-6 rounded-2xl bg-slate-50 p-4 text-xs leading-6 text-slate-600 border border-slate-200">
            {import.meta.env.DEV && (
              <>
            <p className="font-bold text-ink mb-2 flex items-center justify-between">
              <span>⚡ 測試快速登入（點擊直接切換）</span>
              <span className="text-[10px] text-teal-600 font-normal bg-teal-50 px-1.5 py-0.5 rounded">免手動輸入</span>
            </p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <button
                type="button"
                className="px-2.5 py-2 text-xs font-bold rounded-xl bg-teal-600 text-white hover:bg-teal-700 transition text-center shadow-xs"
                onClick={async () => {
                  setUsername('buyer');
                  setPassword('DemoPass123!');
                  setLoading(true);
                  try {
                    const result = await api.auth.login('buyer', 'DemoPass123!');
                    onLogin(result.user);
                    notify('已切換為「王採購 (BUYER)」登入。');
                  } catch (err: any) {
                    notify(err?.message || '登入失敗', 'error');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                🛒 王採購
              </button>
              <button
                type="button"
                className="px-2.5 py-2 text-xs font-bold rounded-xl bg-slate-800 text-white hover:bg-slate-900 transition text-center shadow-xs"
                onClick={async () => {
                  setUsername('admin');
                  setPassword('DemoPass123!');
                  setLoading(true);
                  try {
                    const result = await api.auth.login('admin', 'DemoPass123!');
                    onLogin(result.user);
                    notify('已切換為「系統管理員 (ADMIN)」登入。');
                  } catch (err: any) {
                    notify(err?.message || '登入失敗', 'error');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                🛠️ 管理員
              </button>
              <button
                type="button"
                className="px-2.5 py-2 text-xs font-bold rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition text-center shadow-xs"
                onClick={async () => {
                  setUsername('store');
                  setPassword('DemoPass123!');
                  setLoading(true);
                  try {
                    const result = await api.auth.login('store', 'DemoPass123!');
                    onLogin(result.user);
                    notify('已切換為「林門市 (STORE)」登入。');
                  } catch (err: any) {
                    notify(err?.message || '登入失敗', 'error');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                🏪 林門市
              </button>
            </div>
              </>
            )}

            <div
              className={`${import.meta.env.DEV ? 'border-t border-slate-200 pt-2.5' : ''} flex items-center justify-between text-[11px]`}
            >
              <span className="text-slate-500">外部廠商入口：</span>
              <div className="flex gap-2">
                <a
                  href="/vendor/proposal"
                  className="font-bold text-teal-700 hover:underline"
                >
                  新品提案填報 ↗
                </a>
                <span className="text-slate-300">｜</span>
                <a
                  href="/vendor/apply"
                  className="font-bold text-teal-700 hover:underline"
                >
                  新廠商申請 ↗
                </a>
              </div>
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [checking, setChecking] = useState(true);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const notify: Notify = useCallback(
    (message, type = 'success') => setToast({ message, type }),
    [],
  );
  useEffect(() => {
    document.title = `${VENDOR_PLATFORM_NAME}｜PharmaFlow`;
  }, [location.pathname]);
  useEffect(() => {
    api.auth
      .me()
      .then((result) => setUser(result.user))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);
  useEffect(() => {
    if (toast) {
      const timer = window.setTimeout(() => setToast(null), 4200);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [toast]);
  const logout = async () => {
    try {
      await api.auth.logout();
    } finally {
      setUser(null);
      navigate('/login');
      notify('已安全登出。', 'info');
    }
  };
  if (checking && !location.pathname.startsWith('/vendor/'))
    return (
      <div className="grid min-h-screen place-items-center bg-sand">
        <Loading />
      </div>
    );
  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <Routes>
        <Route path="/internal/password-reset" element={<InternalPasswordResetPage />} />
        <Route path="/vendor/apply" element={<VendorApplicationPageV2 notify={notify} />} />
        <Route path="/vendor/proposal/:id" element={<VendorProposalEditorPage notify={notify} />} />
        <Route path="/vendor/proposal" element={<VendorPortalPage notify={notify} />} />
        <Route path="/vendor/:token" element={<VendorSubmissionPage notify={notify} />} />
        <Route
          path="/login"
          element={
            user ? <Navigate to="/" replace /> : <LoginPage onLogin={setUser} notify={notify} />
          }
        />
        <Route
          element={
            user ? (
              <ProtectedLayout user={user} onLogout={logout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          <Route path="/" element={<DashboardPage notify={notify} />} />
          <Route path="/rounds" element={<RoundsPage notify={notify} />} />
          <Route path="/rounds/new" element={<RoundEditorPage notify={notify} />} />
          <Route path="/rounds/:id/edit" element={<RoundEditorPage notify={notify} />} />
          <Route path="/vendors" element={<VendorsPage notify={notify} />} />
          <Route
            path="/vendor-change-requests"
            element={<VendorChangeRequestsPage notify={notify} />}
          />
          <Route path="/vendor-applications" element={<VendorApplicationsPage notify={notify} />} />
          <Route
            path="/vendor-applications/:id"
            element={<VendorApplicationsPage notify={notify} />}
          />
          <Route path="/proactive" element={<ProactiveInboxPage notify={notify} />} />
          <Route path="/proactive/:id" element={<ProactiveReviewPage notify={notify} />} />
          <Route
            path="/product-master-work-items"
            element={<ProductMasterWorkItemsPage notify={notify} />}
          />
          <Route
            path="/product-master-work-items/:id"
            element={<ProductMasterWorkItemPage notify={notify} />}
          />
          <Route path="/product-masters" element={<FormalProductMastersPage notify={notify} />} />
          <Route path="/compare" element={<ComparePage notify={notify} />} />
          <Route path="/meeting" element={<MeetingPage notify={notify} />} />
          <Route path="/reports" element={<ReportsPage notify={notify} />} />
          <Route path="/proactive-reports" element={<ProactiveReportsPage notify={notify} />} />
          <Route
            path="/launch-announcements"
            element={<LaunchAnnouncementsPage notify={notify} />}
          />
          <Route path="/settings" element={<SettingsPage notify={notify} />} />
          <Route
            path="/product-categories"
            element={
              user?.role === 'ADMIN' ? (
                <ProductCategoriesPage notify={notify} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/product-subcategories"
            element={
              user?.role === 'ADMIN' ? (
                <ProductSubcategoriesPage notify={notify} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="/audit" element={<AuditPage notify={notify} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}

function ProtectedLayout({ user, onLogout }: { user: User; onLogout: () => void }) {
  return (
    <Shell user={user} onLogout={onLogout}>
      <Outlet />
    </Shell>
  );
}
